import { booleanAttribute, ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, output, signal, untracked } from '@angular/core';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { interval, Subscription } from 'rxjs';
import { CartItem } from '../../../models/cart.model';
import { Partner } from '../../../models/global';

@Component({
  selector: 'app-reservation-countdown',
  imports: [NgbTooltipModule, TranslateModule],
  templateUrl: './reservation-countdown.component.html',
  styleUrl: './reservation-countdown.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReservationCountdownComponent {

  readonly showReservationCountdown = input(false, { transform: booleanAttribute });
  readonly partner = input<Partner>();
  readonly cart = input<CartItem[]>();
  readonly reservationExpiredEvent = output<boolean>();

  private readonly destroyRef = inject(DestroyRef);

  protected readonly remainingTimeText = signal<string | null>('');
  protected readonly isReservationExpired = signal(false);

  // Re-derived whenever the cart or partner input changes, so a reservation refreshed while
  // this instance stays mounted (e.g. a related service added on the summary step) restarts
  // the countdown instead of leaving it stuck on the old deadline.
  private readonly deadline = computed<number | null>(() => {
    const partner = this.partner();
    const start = this.cart()?.[0]?.reservation_start_time;
    if (!this.showReservationCountdown() || !partner?.is_appointment_reservation_enabled || !partner.x_minutes_appointment_reservation || !start) {
      return null;
    }
    return new Date(start).getTime() + partner.x_minutes_appointment_reservation * 60 * 1000;
  });

  private countdownSub: Subscription | undefined;
  private lastEmittedExpired: boolean | undefined;

  constructor() {
    effect(() => {
      const endTime = this.deadline();
      untracked(() => this.restartCountdown(endTime));
    });
    this.destroyRef.onDestroy(() => this.countdownSub?.unsubscribe());
  }

  private restartCountdown(endTime: number | null): void {
    this.countdownSub?.unsubscribe();
    if (endTime === null) {
      return;
    }

    this.tick(endTime);

    if (!this.isReservationExpired()) {
      this.countdownSub = interval(1000).subscribe(() => this.tick(endTime));
    }
  }

  private tick(endTime: number): void {
    const remainingMs = endTime - Date.now();

    if (remainingMs > 0) {
      const minutes = Math.floor((remainingMs / 1000 / 60) % 60);
      const seconds = Math.floor((remainingMs / 1000) % 60);
      this.remainingTimeText.set(`${this.pad(minutes)}:${this.pad(seconds)}`);
      this.isReservationExpired.set(false);
    } else {
      this.isReservationExpired.set(true);
      this.remainingTimeText.set(null);
      this.countdownSub?.unsubscribe();
    }

    this.emitExpiredStateOnChange();
  }

  // Emit only on transitions: a per-tick emit(false) would clear the parent's
  // disableBookingButton while a booking request is still in flight.
  private emitExpiredStateOnChange(): void {
    const expired = this.isReservationExpired();
    if (expired === this.lastEmittedExpired) {
      return;
    }
    this.lastEmittedExpired = expired;
    this.reservationExpiredEvent.emit(expired);
  }

  private pad(n: number): string {
    return n < 10 ? '0' + n : n.toString();
  }
}
